import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { downloadFile } from "@/modules/common/utils";
import { DrawingRequest, DrawingRequestStatus } from "../../types";

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getStatusColor(status: string) {
  switch (status) {
    case DrawingRequestStatus.PENDING_ARCHITECTURE:
      return { bg: "#FFF3E0", color: "#E65100" };
    default:
      return { bg: "#E8F5E9", color: "#2E7D32" };
  }
}

function formatStatusLabel(status: string) {
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function DrawingRequestCard({
  request,
  onMarkAsFinal,
  isMarkingFinal,
  anyApproved,
  onUnmarkAsFinal,
  isUnmarkingFinal,
  isContractSigned,
  isActionsBlocked,
}: Readonly<{
  request: DrawingRequest;
  onMarkAsFinal: (uid: string) => void;
  isMarkingFinal: boolean;
  anyApproved: boolean;
  onUnmarkAsFinal: (uid: string) => void;
  isUnmarkingFinal: boolean;
  isContractSigned: boolean;
  isActionsBlocked: boolean;
}>) {
  const theme = useTheme();
  const statusColors = getStatusColor(request.status);
  const isCompleted = request.status === DrawingRequestStatus.COMPLETED;

  return (
    <Box
      sx={{
        bgcolor: theme.nawy.colors.semantic.layer.primary,
        borderRadius: theme.nawy.radius.xl,
        p: theme.nawy.spacing.md,
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.sm,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          sx={(th) => th.nawy.typography.english.variants.headline.xs}
        >
          {request.description || "No description"}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: statusColors.bg,
              color: statusColors.color,
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {formatStatusLabel(request.status)}
          </Box>
        </Stack>
      </Stack>

      <Stack direction="row" gap={theme.nawy.spacing["2xl"]} flexWrap="wrap">
        {request.dueDate && (
          <Stack>
            <Typography
              fontSize={11}
              color={theme.nawy.colors.primitives.grey[700]}
            >
              Due Date
            </Typography>
            <Typography fontSize={14}>{formatDate(request.dueDate)}</Typography>
          </Stack>
        )}
        <Stack>
          <Typography
            fontSize={11}
            color={theme.nawy.colors.primitives.grey[700]}
          >
            Created At
          </Typography>
          {/* Show timestamp (date and time), not just date */}
          <Typography
            fontSize={12}
            color={theme.nawy.colors.primitives.grey[600]}
          >
            {new Date(request.createdAt).toLocaleString()}
          </Typography>
        </Stack>

        {isCompleted && !request.isApprovedForExecution && !anyApproved && (
          <Button
            size="small"
            variant="outlined"
            disabled={isMarkingFinal || isActionsBlocked}
            onClick={() => onMarkAsFinal(request.uid)}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            {isMarkingFinal ? "Marking..." : "Mark as Final"}
          </Button>
        )}
        {request.isApprovedForExecution && !isContractSigned && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={isUnmarkingFinal || isActionsBlocked}
            onClick={() => onUnmarkAsFinal(request.uid)}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            {isUnmarkingFinal ? "Removing..." : "Unselect"}
          </Button>
        )}
      </Stack>

      <Button
        variant="outlined"
        size="small"
        sx={{ alignSelf: "flex-start", textTransform: "none" }}
        disabled={!request.docs?.length}
        onClick={() => {
          const doc = request.docs?.[0];
          if (doc) {
            downloadFile(doc.url);
          }
        }}
      >
        Download developer attachment
      </Button>

      <Stack
        gap={theme.nawy.spacing.xs}
        sx={{
          borderTop: `1px solid ${theme.nawy.colors.primitives.grey[300]}`,
          pt: theme.nawy.spacing.sm,
        }}
      >
        <Typography
          fontSize={12}
          fontWeight={600}
          color={theme.nawy.colors.primitives.grey[700]}
        >
          Proposal
        </Typography>
        {request.finishingDocs?.length ? (
          request.finishingDocs.map((doc) => (
            <Stack
              key={doc.uid}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={theme.nawy.spacing.sm}
            >
              <Typography fontSize={13}>{doc.fileName}</Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{ textTransform: "none" }}
                onClick={() => downloadFile(doc.url)}
              >
                Download
              </Button>
            </Stack>
          ))
        ) : (
          <Typography
            fontSize={12}
            color={theme.nawy.colors.primitives.grey[700]}
          >
            No finishing docs
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
